import { useState, useCallback } from 'react';
import { logger } from '@core/utils';
import { useMoveFileToFolderMutation } from '../api/filesApi';

/**
 * Custom hook for drag and drop functionality
 * Handles dragging files and dropping them onto folders
 *
 * @param {Object} params
 * @param {Function} params.onSuccess - Callback when move succeeds
 * @param {Function} params.onError - Callback when move fails
 *
 * @returns {Object} Drag and drop handlers and state
 *
 * @example
 * const { draggingFileId, handleDragStart, handleDragOver, handleDrop, isDragging } = useDragAndDrop({
 *   onSuccess: () => toast.success('File moved'),
 *   onError: (err) => toast.error(err)
 * });
 */
export function useDragAndDrop({ onSuccess, onError }) {
  const [draggingFileId, setDraggingFileId] = useState(null);
  const [moveFileToFolder] = useMoveFileToFolderMutation();

  /**
   * Handle drag start - stores file ID being dragged
   * @param {Object} file - File object being dragged
   * @param {Event} e - Drag event
   */
  const handleDragStart = useCallback((file, e) => {
    if (file.type === 'file') {
      setDraggingFileId(file.id);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('fileId', file.id);
      logger.info('Drag started:', { fileId: file.id });
    }
  }, []);

  /**
   * Handle drag over - allows drop on folders
   * @param {Object} item - Item being dragged over (folder or file)
   * @param {Event} e - Drag event
   */
  const handleDragOver = useCallback((item, e) => {
    // Only allow drop on folders
    if (item.type === 'folder') {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    }
  }, []);

  /**
   * Handle drop - moves file to target folder
   * @param {Object} targetFolder - Folder where file is dropped
   * @param {Event} e - Drop event
   */
  const handleDrop = useCallback(async (targetFolder, e) => {
    e.preventDefault();

    const fileId = e.dataTransfer.getData('fileId');

    if (!fileId || targetFolder.type !== 'folder') {
      logger.warn('Invalid drop operation');
      return;
    }

    try {
      logger.info('Moving file:', { fileId, targetFolderId: targetFolder.id });

      await moveFileToFolder({
        fileId,
        folderId: targetFolder.id,
      }).unwrap();

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      logger.error('Error moving file:', err);
      if (onError) {
        onError(err.data?.error || 'Error moving file');
      }
    } finally {
      setDraggingFileId(null);
    }
  }, [moveFileToFolder, onSuccess, onError]);

  /**
   * Handle drag end - cleanup dragging state
   */
  const handleDragEnd = useCallback(() => {
    setDraggingFileId(null);
  }, []);

  /**
   * Check if currently dragging
   */
  const isDragging = draggingFileId !== null;

  return {
    draggingFileId,
    isDragging,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  };
}
